import { Box, Table, TableBody, TableHead } from '@mui/material'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useLicensingPaymentBreakdownCalculations } from '../../../../hooks/useLicensingPaymentBreakdownCalculations'
import { LicensingBill, LicensingPayment } from '../../../../swagger'
import {
  BreakDownLedgerPDFVariants,
  BreakdownLedgerForPDF,
} from './BreakdownLedgerForPDF'
import { styles } from './licensingBillingPdfStyles'
import { LicensingFeeSubTotalBreakdown } from './LicensingFeeSubTotalBreakdown'
import { SemesterOneSubtotalBreakdown } from './SemesterOneSubtotalBreakdown'
import { SemesterTwoSubtotalBreakdown } from './SemesterTwoSubtotalBreakdown'
import {
  TableHeaderForPDF,
  TableHeaderForPDFProps,
} from '../../../Table/TableHeaderForPDF'
import { dateToDashString, IS_UTC } from '../../../../utils/dateUtility'

export interface LicensingBreakdownProps {
  licensingBill: LicensingBill
  licensingHistory: LicensingPayment[]
}

export const LicensingPaymentBreakdownForPDF: React.FC<
  LicensingBreakdownProps
> = ({ licensingBill, licensingHistory }) => {
  const { t } = useTranslation()

  const programName = licensingBill._for.programType ?? ''
  const currencyCode = licensingBill.total.currencyCode
  const isChallenge = programName.includes('Challenge')
  const semesterOneStartDate = licensingBill._for.semesterOneStartDate
  const paymentList = licensingHistory.filter(
    (payment) =>
      payment.licensingPayment.programKey === licensingBill._for.programKey
  )

  const tableHeaders: TableHeaderForPDFProps['tableHeaders'] = [
    {
      label: `${programName} - ${
        semesterOneStartDate
          ? dateToDashString(semesterOneStartDate, IS_UTC)
          : ''
      }`,
      align: 'left',
      cssProps: styles.tableHeaderCell,
    },
    {
      label: t('LicensingPayment.Breakdown.Header.FeeAmount', 'Fee Amount'),
      align: 'right',
      cssProps: styles.tableHeaderCell,
    },
    {
      label: t(
        'LicensingPayment.Breakdown.Header.NumberOfStudents',
        '# of Students'
      ),
      align: 'right',
      cssProps: styles.tableHeaderCell,
    },
    {
      label: t('LicensingPayment.Breakdown.Header.Subtotal', 'Subtotal'),
      align: 'right',
      cssProps: styles.tableHeaderCell,
    },
  ]

  const {
    balance,
    licensingFeeAdditionalStudent,
    licensingFeeFirstStudent,
    licensingFeeSubtotal,
    owed,
    paid,
    semester1AdditionalStudent,
    semester1FirstStudent,
    semester1Subtotal,
    semester2AdditionalStudent,
    semester2FirstStudent,
    semester2Subtotal,
  } = useLicensingPaymentBreakdownCalculations({ licensingBill })

  return (
    <Box style={styles.table}>
      <Table style={styles.licensingPaymentBreakdownTable}>
        <TableHead>
          <TableHeaderForPDF tableHeaders={tableHeaders} />
        </TableHead>

        <TableBody>
          <LicensingFeeSubTotalBreakdown
            licensingFeeSubtotal={licensingFeeSubtotal}
            licensingFeeFirstStudent={licensingFeeFirstStudent}
            licensingFeeAdditionalStudent={licensingFeeAdditionalStudent}
          />
          <SemesterOneSubtotalBreakdown
            semester1AdditionalStudent={semester1AdditionalStudent}
            semester1FirstStudent={semester1FirstStudent}
            semester1Subtotal={semester1Subtotal}
            isChallenge={isChallenge}
          />
          <SemesterTwoSubtotalBreakdown
            semester2Subtotal={semester2Subtotal}
            semester2FirstStudent={semester2FirstStudent}
            semester2AdditionalStudent={semester2AdditionalStudent}
            isChallenge={isChallenge}
          />
          <BreakdownLedgerForPDF
            variant={BreakDownLedgerPDFVariants.Balance}
            totalOwed={owed}
            totalPaid={paid}
            balance={balance}
            licensingDue={licensingBill.licensingDue?.amount ?? 0}
            currencyCode={currencyCode}
            programName={programName}
            paymentList={paymentList}
          />
        </TableBody>
      </Table>
    </Box>
  )
}
